import type { NetworkMovementInput, NetworkPlayer } from './protocol';

const motionControls = ['forward', 'right', 'jump', 'grab', 'sprint'] as const;

/** State transitions need authority now, not after a possibly stalled paint.
 * Position/velocity, look, heartbeats and other runners retain normal batching.
 * This changes delivery only: no predicted state, clock, force or action is made.
 */
export function ownerMotionChanged(
  previous: NetworkPlayer | null,
  next: NetworkPlayer | undefined,
  latestSentInput: Readonly<NetworkMovementInput> | null = null,
): boolean {
  if (!previous || !next || previous.id !== next.id) return false;
  if (previous.falls !== next.falls || (previous.trainNumber ?? 1) !== (next.trainNumber ?? 1)
    || previous.extracted !== next.extracted || previous.grounded !== next.grounded
    || Boolean(previous.climbing) !== Boolean(next.climbing)
    || (previous.grabbedById ?? '') !== (next.grabbedById ?? '')
    || (previous.grabTargetId ?? '') !== (next.grabTargetId ?? '')
    || Boolean(previous.gadgets?.grappleActive) !== Boolean(next.gadgets?.grappleActive)) return true;
  const accepted = previous.predictionState?.input, current = next.predictionState?.input;
  // A delayed old heartbeat can revive a neutralized lease after the runner
  // has already reversed. Do not force that obsolete control through a burst
  // immediately before the newer acceptance. It still uses normal bounded
  // delivery; physical/life transitions above and force events remain urgent.
  if (current && latestSentInput && next.lastInput < latestSentInput.seq
    && motionControls.some(key => current[key] !== latestSentInput[key])) return false;
  return Boolean(accepted && current && motionControls.some(key => accepted[key] !== current[key]));
}
